import React, { useEffect } from 'react';
import { styled } from 'styled-components';
import { Tooltip } from 'react-tooltip';
import { strings } from '/imports/strings';
import { fileHref } from '/imports/api/files';
import { Select } from '/imports/ui/Select';
import { useFind, useFindOne, useMethod, useUser, useIsLuminia } from '/imports/hooks';
import { NumberInput } from '/imports/ui/NumberInput';
import { copyToClipboard, filterBoolean, formatCurrency, formatDate, formatNumber, mapById, objectKeys, optionsToDict, uniqueArray } from '/imports/utils';
import { Input } from '/imports/ui/Input';
import { Dict } from '/imports/types';
import { InfoIcon } from '/imports/ui/InfoIcon';
import { FormError } from '/imports/ui/FormError';
import { Orgs } from '/imports/api/orgs';
import { RichSelect } from '/imports/ui/RichSelect';
import { RichMultiSelect } from '/imports/ui/RichMultiSelect';
import { accountDisplayName, Accounts, listAccountsForOrg, Roles } from '/imports/api/accounts';
import {
  BuildingTypeOptions, essCapacityOptions, InstallTypeOptions, OwnershipTypeOptions, Project, twoHourBatteryMap
} from '/imports/api/projects';

const ownershipTypeStrings = optionsToDict(OwnershipTypeOptions);

const buildingTypes: Dict<string> = {
  ...BuildingTypeOptions.reduce((m: Dict<string>, o) => { m[o.value] = o.label; return m; }, {})
};

export const ProjectDetailsStyled = styled.div`
  .infoblock {
    display: inline-block;
    margin-right: 2rem;
    margin-bottom: 1rem;
    width: 30%;
    vertical-align: top;
    .label {
      font-weight: bold;
    }
    input, select {
      max-width: 100%;
      box-sizing: border-box;
    }
    .line {
      overflow: hidden;
    }
    .input {
      width: 100%;
      input {
        width: 100%;
      }
    }
    .error {
      margin-top: 0.25rem;
    }
  }
  [icon="help"] {
    position: relative;
    top: 0.375rem;
  }
  .building-type-select {
    .select-all {
      display: none;
    }
  }
`;

export interface ProjectDetailsProps {
  project: Project;
  editing: boolean;
  update: Partial<Project>;
  setUpdate: React.Dispatch<React.SetStateAction<Partial<Project>>>;
  error?: any;
}

/**
 * Project info section (address, utility, ownership, ITC eligibility, etc.) with super-user inline editing.
 */
export const ProjectDetails = (props: ProjectDetailsProps) => {
  const user = useUser();
  const userId = user?._id;
  const isLuminia = useIsLuminia();
  const isSuperUser = user?.isSuperUser;  // the org move alone stays a super-user act
  const orgId = user?.orgId;
  const orgs = useFind(() => Orgs.find(), [user?._id]);
  const orgMap = mapById(orgs);
  const account = useFindOne(() => Accounts.find({ userId, orgId }), [userId, orgId]);
  const [accounts,] = useMethod(listAccountsForOrg, user?.orgId, [user?.orgId], () => !!user?.orgId);
  const { project, editing, update, setUpdate, error } = props;
  const solarEpcItcEligible = update.solarEpcItcEligible !== undefined ? update.solarEpcItcEligible : project.solarEpcItcEligible;
  const solarEnergyCommunityAdder = update.solarEnergyCommunityAdder !== undefined ? update.solarEnergyCommunityAdder : project.solarEnergyCommunityAdder;
  const solarDomesticContentAdder = update.solarDomesticContentAdder !== undefined ? update.solarDomesticContentAdder : project.solarDomesticContentAdder;
  const storageEpcItcEligible = update.storageEpcItcEligible !== undefined ? update.storageEpcItcEligible : project.storageEpcItcEligible;
  const storageEnergyCommunityAdder = update.storageEnergyCommunityAdder !== undefined ? update.storageEnergyCommunityAdder : project.storageEnergyCommunityAdder;
  const storageDomesticContentAdder = update.storageDomesticContentAdder !== undefined ? update.storageDomesticContentAdder : project.storageDomesticContentAdder;
  // TODO: give each section its own update/setUpdate, merge via an onSave(parentUpdate) prop before saving.
  return (
    <ProjectDetailsStyled>
      <h3>{strings.ProjectInfo()}</h3>
      <div className="infoblock">
        <div className="label">{strings.Address()}</div>
        <div className="value">
          {/* {editing ? <AddressInput inline value={update.address || project.address} onChange={(address) => setUpdate({ ...update, address })} /> : project.address?.formatted} */}
          {project.address?.formatted}
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.County()}</div>
        <div className="value">
          {
            editing && isLuminia ?
              <Input id="proj-county" value={update.county || project.county} onChange={(county) => setUpdate({ ...update, county })} />
              :
              project.county
          }
        </div>
        <FormError field="county" error={error} />
      </div>
      <div className="infoblock">
        <div className="label">{strings.UtilityProvider()}</div>
        <div className="value">
          {
            editing && isLuminia ?
              <Input id="proj-utilities" value={update.utilities ? update.utilities?.join(', ') : project.utilities?.join(', ')} onChange={(u) => setUpdate({ ...update, utilities: u?.split(',').map((u) => u.trim()) })} />
              :
              project.utilities?.join(', ')
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.CcaServiceTerritory()}</div>
        <div className="value">
          {
            editing && isLuminia ?
              <Input id="proj-ccas" value={update.ccas ? update.ccas?.join(', ') : project.ccas?.join(', ')} onChange={(u) => setUpdate({ ...update, ccas: u?.split(',').map((u) => u.trim()) })} />
              :
              project.ccas?.join(', ')
          }
        </div>
      </div>
      {/* <div className="infoblock">
        <div className="label">{strings.ProposalType()}</div>
        <div className="value">
          {
            editing ?
              <Select value={update.proposalType || project.proposalType} onChange={(proposalType) => setUpdate({ ...update, proposalType })}
                options={ProposalTypeOptions}
              />
              :
              proposalTypeStrings[project.proposalType] || project.proposalType
          }
        </div>
      </div> */}
      <div className="infoblock">
        <div className="label">{strings.EstimatedAnnualConsumption()} (kWh)</div>
        <div className="value">
          {
            editing ?
              <NumberInput id="proj-consumption" value={update.annualConsumption ? update.annualConsumption : project.annualConsumption} onChange={(c) => setUpdate({ ...update, annualConsumption: c })} />
              :
              formatNumber(project.annualConsumption)
          }
        </div>
        <FormError field="annualConsumption" error={error} />
      </div>
      <div className="infoblock">
        <div className="label">{strings.PeakLoadDemand()} (kW)</div>
        <div className="value">
          {
            editing && isLuminia ?
              <NumberInput id="proj-max-demand" value={update.maxDemand ? update.maxDemand : project.maxDemand} onChange={(c) => setUpdate({ ...update, maxDemand: c })} />
              :
              formatNumber(project.maxDemand)
          }
        </div>
        <FormError field="maxDemand" error={error} />
      </div>
      <div className="infoblock">
        <div className="label">{strings.OwnershipType()}</div>
        <div className="value">
          {
            editing ?
              <Select id="proj-ownership" value={update.ownershipType ? update.ownershipType : project.ownershipType} onChange={(ownershipType: any) => setUpdate({ ...update, ownershipType, siteLeasePayment: ownershipType === 'owner' ? 'Minimal' : ownershipType === 'tenant' ? 'Full' : update.siteLeasePayment })}
                options={OwnershipTypeOptions}
              />
              :
              ownershipTypeStrings[project.ownershipType as string] || project.ownershipType
          }
        </div>
        <FormError field="ownershipType" error={error} />
      </div>
      <div className="infoblock">
        <div className="label">{strings.Organization()}</div>
        <div className="value">
          {
            editing && isSuperUser ?
              <RichSelect id="proj-org" value={update.orgId || project.orgId} onChange={(option) => setUpdate({ ...update, orgId: option.value })}
                options={orgs.map((o) => ({ value: o._id, label: o.name || o._id }))}
              />
              :
              orgMap[user?.orgId || '']?.name
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.AssignedTo()}</div>
        <div className="value">
          {
            editing && (isLuminia || account?.roles?.includes(Roles.admin)) ?
              <RichSelect id="proj-assigned" value={update.assignedToAccountId || project.assignedToAccountId} onChange={(option) => setUpdate({ ...update, assignedToAccountId: option.value })}
                options={(accounts || []).map((o) => ({ value: o._id, label: accountDisplayName(o) || o._id }))}
              />
              :
              accountDisplayName(project._assignedToAccount)
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.PtoYear()}</div>
        <div className="value">
          {
            editing && isLuminia ?
              <NumberInput id="proj-pto-year" value={update.ptoYear ? update.ptoYear : project.ptoYear} onChange={(c) => setUpdate({ ...update, ptoYear: c })} />
              :
              project.ptoYear
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.SolarEpcCostItcEligible()}?</div>
        <div className="value">
          {
            editing && isLuminia ?
              <Select id="proj-solar-itc" value={solarEpcItcEligible ? 'yes' : 'no'} onChange={(solarEpcItcEligible: any) => setUpdate({ ...update, solarEpcItcEligible: solarEpcItcEligible === 'yes' ? true : false })}
                options={[{ value: 'yes', label: strings.Yes() }, { value: 'no', label: strings.No() }]}
              />
              :
              project.solarEpcItcEligible ? strings.Yes() : strings.No()
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.SolarEnergyCommunityAdder()}?</div>
        <div className="value">
          {
            editing && isLuminia ?
              <Select id="proj-solar-energy-community" value={solarEnergyCommunityAdder ? 'yes' : 'no'} onChange={(solarEnergyCommunityAdder: any) => setUpdate({ ...update, solarEnergyCommunityAdder: solarEnergyCommunityAdder === 'yes' ? true : false })}
                options={[{ value: 'yes', label: strings.Yes() }, { value: 'no', label: strings.No() }]}
              />
              :
              project.solarEnergyCommunityAdder ? strings.Yes() : strings.No()
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.SolarDomesticContentAdder()}?</div>
        <div className="value">
          {
            editing && isLuminia ?
              <Select id="proj-solar-domestic-content" value={solarDomesticContentAdder ? 'yes' : 'no'} onChange={(solarDomesticContentAdder: any) => setUpdate({ ...update, solarDomesticContentAdder: solarDomesticContentAdder === 'yes' ? true : false })}
                options={[{ value: 'yes', label: strings.Yes() }, { value: 'no', label: strings.No() }]}
              />
              :
              project.solarDomesticContentAdder ? strings.Yes() : strings.No()
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.StorageEpcCostItcEligible()}?</div>
        <div className="value">
          {
            editing && isLuminia ?
              <Select id="proj-storage-itc" value={storageEpcItcEligible ? 'yes' : 'no'} onChange={(storageEpcItcEligible: any) => setUpdate({ ...update, storageEpcItcEligible: storageEpcItcEligible === 'yes' ? true : false })}
                options={[{ value: 'yes', label: strings.Yes() }, { value: 'no', label: strings.No() }]}
              />
              :
              project.storageEpcItcEligible ? strings.Yes() : strings.No()
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.StorageEnergyCommunityAdder()}?</div>
        <div className="value">
          {
            editing && isLuminia ?
              <Select id="proj-storage-energy-community" value={storageEnergyCommunityAdder ? 'yes' : 'no'} onChange={(storageEnergyCommunityAdder: any) => setUpdate({ ...update, storageEnergyCommunityAdder: storageEnergyCommunityAdder === 'yes' ? true : false })}
                options={[{ value: 'yes', label: strings.Yes() }, { value: 'no', label: strings.No() }]}
              />
              :
              project.storageEnergyCommunityAdder ? strings.Yes() : strings.No()
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.StorageDomesticContentAdder()}?</div>
        <div className="value">
          {
            editing && isLuminia ?
              <Select id="proj-storage-domestic-content" value={storageDomesticContentAdder ? 'yes' : 'no'} onChange={(storageDomesticContentAdder: any) => setUpdate({ ...update, storageDomesticContentAdder: storageDomesticContentAdder === 'yes' ? true : false })}
                options={[{ value: 'yes', label: strings.Yes() }, { value: 'no', label: strings.No() }]}
              />
              :
              project.storageDomesticContentAdder ? strings.Yes() : strings.No()
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.SiteLeasePayment()}?</div>
        <div className="value">
          {
            editing && isLuminia ?
              <Select id="proj-lease" value={update.siteLeasePayment ? update.siteLeasePayment : project.siteLeasePayment} onChange={(siteLeasePayment: string) => setUpdate({ ...update, siteLeasePayment })}
                options={[{ value: 'Minimal', label: strings.Minimal() }, { value: 'Full', label: strings.Full() }]}
              />
              :
              project.siteLeasePayment === 'Minimal' ? strings.Minimal() : strings.Full()
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.CreatedAt()}</div>
        <div className="value">{formatDate(project.createdAt)}</div>
      </div>
    </ProjectDetailsStyled>
  );
};

const installTypeStrings = InstallTypeOptions.reduce((map: Dict<string>, opt) => {
  map[opt.value] = opt.label;
  return map;
}, {});

/**
 * Solar system section (system size, production, EPC price, install/building type) with editing.
 */
export const SystemDetails = (props: ProjectDetailsProps) => {
  const { project, editing, update, setUpdate, error } = props;
  const user = useUser();
  const isLuminia = useIsLuminia();
  const org = useFindOne(() => Orgs.find(user?.orgId), [user?.orgId]);
  const isDealer = org.projectChannel === 'dealer';
  return (
    <ProjectDetailsStyled>
      <h3>{strings.SolarSystem()}</h3>
      <div className="infoblock">
        <div className="label">{strings.SystemSize()} (kW DC)<InfoIcon tip={strings.ProjectSystemSizeTip()} /></div>
        <div className="value">
          {
            editing ?
              <NumberInput id="sys-size" value={update.systemSize || project.systemSize} onChange={(systemSize) => setUpdate({ ...update, systemSize })} />
              :
              formatNumber(project.systemSize)
          }
        </div>
        <FormError field="systemSize" error={error} />
      </div>
      <div className="infoblock">
        <div className="label">{strings.AnnualProduction()} (kWh)<InfoIcon tip={strings.ProjectAnnualProductionTip()} /></div>
        <div className="value">
          {
            editing ?
              <NumberInput id="sys-production" value={update.annualProduction || project.annualProduction} onChange={(annualProduction) => setUpdate({ ...update, annualProduction })} />
              :
              formatNumber(project.annualProduction)
          }
        </div>
        <FormError field="annualProduction" error={error} />
      </div>
      <div className="infoblock">
        <div className="label">{strings.EpcPrice()} ($/W)<InfoIcon tip={strings.ProjectEpcPriceTip()} /></div>
        <div className="value">
          {
            editing && (isLuminia || isDealer) ?
              <NumberInput id="sys-epc-price" value={update.epcPrice || project.epcPrice} onChange={(epcPrice) => setUpdate({ ...update, epcPrice })} />
              :
              formatNumber(project.epcPrice, 2)
          }
        </div>
        <FormError field="epcPrice" error={error} />
      </div>
      <div className="infoblock">
        <div className="label">{strings.AvoidedCost()} ($/kWh)<InfoIcon tip={strings.ProjectAvoidedCostTip()} /></div>
        <div className="value">
          {
            editing && (isLuminia || isDealer) ?
              <NumberInput id="sys-avoided-cost" value={update.avoidedCost || project.avoidedCost} onChange={(avoidedCost) => setUpdate({ ...update, avoidedCost })} />
              :
              formatNumber(project.avoidedCost, 3)
          }
        </div>
        <FormError field="avoidedCost" error={error} />
      </div>
      <div className="infoblock">
        <div className="label">{strings.InstallType()}<InfoIcon tip={strings.ProjectSystemTypeTip()} /></div>
        <div className="value">
          {
            editing ?
              <Select id="sys-install-type" value={update.installType || project.installType} onChange={(installType: any) => setUpdate({ ...update, installType })}
                options={InstallTypeOptions}
              />
              :
              installTypeStrings[project.installType || ''] || project.installType
          }
        </div>
        <FormError field="installType" error={error} />
      </div>
      <div className="infoblock">
        <div className="label">{strings.BuildingType()}<InfoIcon tip={strings.ProjectBuildingTypeTip()} /></div>
        <div className="value">
          {
            editing ?
              <RichMultiSelect className="building-type-select" selectedValues={update.buildingTypes || project.buildingTypes || []} onSelect={(buildingTypes) => setUpdate({ ...update, buildingTypes })}
                options={objectKeys(buildingTypes).map((k) => ({ value: k, label: buildingTypes[k] }))}
              />
              :
              update.buildingTypes === undefined ?
                filterBoolean((project.buildingTypes || []).map((t) => buildingTypes[t])).join(', ')
                :
                filterBoolean((update.buildingTypes || []).map((t) => buildingTypes[t])).join(', ')
          }
        </div>
      </div>
    </ProjectDetailsStyled>
  );
};

/**
 * Energy storage section (ESS size, capacity, EPC price, avoided cost) with editing.
 */
export const StorageDetails = (props: ProjectDetailsProps) => {
  const { project, editing, update, setUpdate, error } = props;
  const essSize = update.essSize !== undefined ? update.essSize : project.essSize;
  const user = useUser();
  const isLuminia = useIsLuminia();
  const org = useFindOne(() => Orgs.find(user?.orgId), [user?.orgId]);
  const isDealer = org.projectChannel === 'dealer';
  useEffect(() => {
    if (!editing) return;
    const essCapacity = parseInt(essCapacityOptions(update.essSize)[0]?.value || '0');
    setUpdate({ ...update, essCapacity });
  }, [update.essSize]);
  return (
    <ProjectDetailsStyled>
      <h3>{strings.EnergyStorageSystem()}</h3>
      <div className="infoblock">
        <div className="label">{strings.EssSize()} (kW)<InfoIcon tip={strings.ProjectEssSizeTip()} /></div>
        <div className="value">
          {
            editing ?
              <Select id="ess-size"
                value={`${update.essSize || project.essSize || ''}`} onChange={(essSize) => setUpdate({ ...update, essSize: parseInt(essSize) })}
                // Sizes come from twoHourBatteryMap (same source as the project form's picker) so this
                // list can't drift from essCapacityOptions — a size with no row there yields an empty
                // capacity dropdown.
                options={[{ value: '', label: '' }, ...objectKeys(twoHourBatteryMap).map((k) => ({ value: k, label: k }))]}
              />
              :
              formatNumber(project.essSize)
          }
        </div>
        <FormError field="essSize" error={error} />
      </div>
      <div className="infoblock">
        <div className="label">{strings.EssCapacity()} (kWh)<InfoIcon tip={strings.ProjectEssCapacityTip()} /></div>
        <div className="value">
          {
            editing ?
              <Select id="ess-capacity"
                disabled={!essSize}
                value={`${update.essCapacity || project.essCapacity || ''}`} onChange={(essCapacity) => setUpdate({ ...update, essCapacity: parseInt(essCapacity) })}
                options={essCapacityOptions(essSize)}
              />
              :
              formatNumber(project.essCapacity)
          }
        </div>
        <FormError field="essCapacity" error={error} />
      </div>
      <div className="infoblock">
        <div className="label">{strings.EssEpcPrice()} ($/kWh)<InfoIcon tip={strings.ProjectEssEpcPriceTip()} /></div>
        <div className="value">
          {
            editing && (isLuminia || isDealer) ?
              <NumberInput id="ess-epc-price" value={update.essEpcPrice || project.essEpcPrice} onChange={(essEpcPrice) => setUpdate({ ...update, essEpcPrice })} />
              :
              formatNumber(project.essEpcPrice)
          }
        </div>
        <FormError field="essEpcPrice" error={error} />
      </div>
      <div className="infoblock">
        <div className="label">{strings.EssAvoidedCost()} ($/kWh)<InfoIcon tip={strings.ProjectEssAvoidedCostTip()} /></div>
        <div className="value">
          {
            editing && (isLuminia || isDealer) ?
              <NumberInput id="ess-avoided-cost" value={update.essAvoidedCost || project.essAvoidedCost} onChange={(essAvoidedCost) => setUpdate({ ...update, essAvoidedCost })} />
              :
              formatNumber(project.essAvoidedCost)
          }
        </div>
      </div>
    </ProjectDetailsStyled>
  );
};

// const AdditionalDetails = (props: ProjectDetailsProps) => {
//   const { project, editing, update, setUpdate } = props;
//   return (
//     <ProjectDetailsStyled>
//       <h3>{strings.AdditionalPricingInputs()}</h3>
//       <div className="infoblock">
//         <div className="label">{strings.InvestmentTaxCreditAdder()} (%)<InfoIcon tip={strings.ProjectInvestmentTaxCreditAdderTip()} /></div>
//         <div className="value">
//           {editing ? <NumberInput value={update.investmentTaxCreditAdder || project.investmentTaxCreditAdder} onChange={(investmentTaxCreditAdder) => setUpdate({ ...update, investmentTaxCreditAdder })} /> : project.investmentTaxCreditAdder}
//         </div>
//       </div>
//       <div className="infoblock">
//         <div className="label">{strings.SolarPpaEscalator()} (%)</div>
//         <div className="value">
//           {editing ? <NumberInput value={update.solarPpaEscalator || project.solarPpaEscalator} onChange={(solarPpaEscalator) => setUpdate({ ...update, solarPpaEscalator })} /> : project.solarPpaEscalator}
//         </div>
//       </div>
//       <div className="infoblock">
//         <div className="label">{strings.PpaTermLengthYears()}</div>
//         <div className="value">
//           {editing ? <NumberInput value={update.ppaTermLength || project.ppaTermLength} onChange={(ppaTermLength) => setUpdate({ ...update, ppaTermLength })} /> : project.ppaTermLength}
//         </div>
//       </div>
//       <div className="infoblock">
//         <div className="label">{strings.BuyoutYear1()}</div>
//         <div className="value">
//           {editing ? <NumberInput value={update.buyoutYear1 || project.buyoutYear1} onChange={(buyoutYear1) => setUpdate({ ...update, buyoutYear1 })} /> : project.buyoutYear1}
//         </div>
//       </div>
//       <div className="infoblock">
//         <div className="label">{strings.BuyoutYear2()}</div>
//         <div className="value">
//           {editing ? <NumberInput value={update.buyoutYear2 || project.buyoutYear2} onChange={(buyoutYear2) => setUpdate({ ...update, buyoutYear2 })} /> : project.buyoutYear2}
//         </div>
//       </div>
//       <h3>{strings.Incentives()}</h3>
//       <h4>{strings.UpFrontGrantRebate()}<InfoIcon tip={strings.UpFrontGrantRebateTip()} /></h4>
//       <div className="infoblock">
//         <div className="label">{strings.SolarSystemIncentive()} ($)</div>
//         <div className="value">
//           {editing ? <NumberInput value={update.solarSystemIncentive || project.solarSystemIncentive} onChange={(solarSystemIncentive) => setUpdate({ ...update, solarSystemIncentive })} /> : project.solarSystemIncentive}
//         </div>
//       </div>
//       <div className="infoblock">
//         <div className="label">{strings.EnergyStorageIncentive()} ($)</div>
//         <div className="value">
//           {editing ? <NumberInput value={update.energyStorageIncentive || project.energyStorageIncentive} onChange={(energyStorageIncentive) => setUpdate({ ...update, energyStorageIncentive })} /> : project.energyStorageIncentive}
//         </div>
//       </div>
//       <div className="infoblock">
//         <div className="label">{strings.OtherIncentive()} ($)</div>
//         <div className="value">
//           {editing ? <NumberInput value={update.otherIncentive || project.otherIncentive} onChange={(otherIncentive) => setUpdate({ ...update, otherIncentive })} /> : project.otherIncentive}
//         </div>
//       </div>
//       <h4>{strings.PerformanceBasedIncentiveRec()}<InfoIcon tip={strings.PerformanceBasedIncentiveRecTip()} /></h4>
//       <div className="infoblock">
//         <div className="label">{strings.IncentiveTerm()}<InfoIcon tip={strings.ProjectIncentiveTermTip()} /></div>
//         <div className="value">
//           {editing ? <NumberInput value={update.incentiveTerm || project.incentiveTerm} onChange={(incentiveTerm) => setUpdate({ ...update, incentiveTerm })} /> : project.incentiveTerm}
//         </div>
//       </div>
//       <div className="infoblock">
//         <div className="label">{strings.AnnualIncentiveEscalation()}<InfoIcon tip={strings.ProjectAnnualIncentiveEscalationTip()} /></div>
//         <div className="value">
//           {editing ? <NumberInput value={update.annualIncentiveEscalation || project.annualIncentiveEscalation} onChange={(annualIncentiveEscalation) => setUpdate({ ...update, annualIncentiveEscalation })} /> : project.annualIncentiveEscalation}
//         </div>
//       </div>
//       <div className="infoblock">
//         <div className="label">{strings.IncentiveRate()} (per MWh)<InfoIcon tip={strings.ProjectIncentiveRateTip()} /></div>
//         <div className="value">
//           {editing ? <NumberInput value={update.incentiveRate || project.incentiveRate} onChange={(incentiveRate) => setUpdate({ ...update, incentiveRate })} /> : project.incentiveRate}
//         </div>
//       </div>
//     </ProjectDetailsStyled>
//   );
// };

const EnrichmentsSectionStyled = styled(ProjectDetailsStyled)`
  .grid {
    display: block;
    grid-template-columns: repeat(3, 1fr);
    max-width: 100%;
  }
  .infoblock {
    max-width: 35rem;
  }
  .value {
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

/**
 * Read-only enrichment data section showing Reonomy, Google, and Energy Toolbase results for a project.
 */
export const EnrichmentsSection = (props: { project: Project }) => {
  const { project } = props;
  const principals = project._reonomy?.principals || [];
  const contact = principals[0];
  const contactName = `${contact?.name?.full} (${uniqueArray(contact?.companies?.map((c: any) => c.title).flat()).slice(0, 6).join(', ')})`;
  const contactPhone = uniqueArray(contact?.phones?.map((p: any) => p.number)).join(', ');
  const contactEmail = uniqueArray(contact?.emails?.map((p: any) => p.address)).join(', ');
  const contactAddress = uniqueArray(contact?.addresses?.map((p: any) => `${p.line1}, ${p.city}, ${p.state} ${p.postal_code}`)).join(', ');
  const reportedOwners = project._reonomy?.owners?.map((o: any) => o.reported_owners)?.flat();
  const naics = project._reonomy?.tenants?.map((t: any) => t.naics).join(', ');
  const naicsDescription = project._reonomy?.tenants?.map((t: any) => t.naics_description).join(', ');
  const sic = project._reonomy?.tenants?.map((t: any) => t.sic).join(', ');
  const sicDescription = project._reonomy?.tenants?.map((t: any) => t.sic_description).join(', ');
  return (
    <EnrichmentsSectionStyled>
      <h3>Reonomy</h3>
      <div className="grid">
        <div className="infoblock">
          <div className="label">{strings.PropertyOwner()}</div>
          <div className="value">{reportedOwners?.map((o: any) => o.name).join(',')}</div>
        </div>
        <div className="infoblock">
          <div className="label">{strings.ContactName()}</div>
          <div className="value" data-tooltip-id="contact-name-tip" data-tooltip-content={contactName} onClick={() => copyToClipboard(contactName)}>{contactName}</div>
          <Tooltip id="contact-name-tip" />
        </div>
        <div className="infoblock">
          <div className="label">{strings.ContactPhone()}</div>
          <div className="value" data-tooltip-id="contact-phone-tip" data-tooltip-content={contactPhone} onClick={() => copyToClipboard(contactPhone)}>{contactPhone}</div>
          <Tooltip id="contact-phone-tip" />
        </div>
        <div className="infoblock">
          <div className="label">{strings.ContactEmail()}</div>
          <div className="value" data-tooltip-id="contact-phone-tip" data-tooltip-content={contactEmail} onClick={() => copyToClipboard(contactEmail)}>{contactEmail}</div>
          <Tooltip id="contact-email-tip" />
        </div>
        <div className="infoblock">
          <div className="label">{strings.ContactAddress()}</div>
          <div className="value" data-tooltip-id="contact-phone-tip" data-tooltip-content={contactAddress} onClick={() => copyToClipboard(contactAddress)}>{contactAddress}</div>
          <Tooltip id="contact-address-tip" />
        </div>
        <div className="infoblock">
          <div className="label">{strings.OwnershipType()}</div>
          <div className="value">{reportedOwners?.map((o: any) => o.entity_type).join(',')}</div>
        </div>
        <div className="infoblock">
          <div className="label">APN</div>
          <div className="value">{project._reonomy?.apn}</div>
        </div>
        <div className="infoblock">
          <div className="label">{strings.BuildingType()}</div>
          <div className="value">{project._reonomy?.asset_type}</div>
        </div>
        <div className="infoblock">
          <div className="label">{strings.OwnerOccupied()}</div>
          <div className="value">{project._reonomy?.ownerOccupied ? strings.Yes() : '-'}</div>
        </div>
        <div className="infoblock">
          <div className="label">{strings.Stories()}</div>
          <div className="value">{project._reonomy?.floors || '-'}</div>
        </div>
        <div className="infoblock">
          <div className="label">{strings.YearBuiltRenovated()}</div>
          <div className="value">{project._reonomy?.year_built || '-'} / {project._reonomy?.year_renovated || '-'}</div>
        </div>
        <div className="infoblock">
          <div className="label">{strings.LotSize()} (sq.ft.)</div>
          <div className="value">{formatNumber(project._reonomy?.lot_size_sqft) || '-'}</div>
        </div>
        <div className="infoblock">
          <div className="label">{strings.BuildingSize()} (sq.ft.)</div>
          <div className="value">{formatNumber(project._reonomy?.building_area) || '-'}</div>
        </div>
        <div className="infoblock">
          <div className="label">{strings.AssessedValue()}</div>
          <div className="value">{formatCurrency(project._reonomy?.last_mortgage_amount) || '-'}</div>
        </div>
        <div className="infoblock">
          <div className="label">NAICS</div>
          <div className="value" data-tooltip-id="naics-tip" data-tooltip-content={naics} onClick={() => copyToClipboard(contactAddress)}>{naics}</div>
          <Tooltip id="naics-tip" />
        </div>
        <div className="infoblock">
          <div className="label">NAICS {strings.Description()}</div>
          <div className="value" data-tooltip-id="naics-description-tip" data-tooltip-content={naicsDescription} onClick={() => copyToClipboard(naicsDescription)}>{naicsDescription}</div>
          <Tooltip id="naics-description-tip" />
        </div>
        <div className="infoblock">
          <div className="label">SIC</div>
          <div className="value" data-tooltip-id="sic-tip" data-tooltip-content={sic} onClick={() => copyToClipboard(sic)}>{sic}</div>
          <Tooltip id="sic-tip" />
        </div>
        <div className="infoblock">
          <div className="label">SIC {strings.Description()}</div>
          <div className="value" data-tooltip-id="sic-description-tip" data-tooltip-content={sicDescription} onClick={() => copyToClipboard(sicDescription)}>{sicDescription}</div>
          <Tooltip id="sic-description-tip" />
        </div>
      </div>
      <h3>Google</h3>
      <div className="grid">
        {/* Roof area comes from the Solar API or the older Sunroof scraper depending on
            google.solarRoofSource, so the source is shown rather than baked into the label. */}
        <div className="infoblock">
          <div className="label">{strings.RooftopArea()} (sq.ft.)</div>
          <div className="value">{formatNumber(project.sunroof?.sqft) || '-'}</div>
        </div>
        <div className="infoblock">
          <div className="label">{strings.Source()}</div>
          <div className="value">{project.sunroof?.source === 'solarApi' ? 'Solar API' : project.sunroof?.source === 'sunroof' ? 'Sunroof' : '-'}</div>
        </div>
        <div className="infoblock">
          <div className="label">{strings.MaxSystemSize()} (kW DC)</div>
          <div className="value">{formatNumber(project.sunroof?.maxKwDc) || '-'}</div>
        </div>
        <div className="infoblock">
          <div className="label">{strings.ExistingSolar()}</div>
          <div className="value">{project.sunroof?.existingSolar == null ? '-' : project.sunroof.existingSolar ? strings.Yes() : strings.No()}</div>
        </div>
        <div className="infoblock">
          <div className="label">{strings.BuildingType()}</div>
          <div className="value">{project._gmapsDestination?.types.join(', ') || project._reonomy?.asset_type || '-'}</div>
        </div>
        <div className="infoblock">
          <div className="label">{strings.Footprint()} (sq.ft.)</div>
          <div className="value">{project._gmapsDestination?.footprintArea || '-'}</div>
        </div>
      </div>
      {project.etb && (
        <>
          <h3>{strings.EnergyToolbase()}</h3>
          <div className="grid">
            <div className="infoblock">
              <div className="label">{strings.SolarAvoidedCost()}</div>
              <div className="value">{formatCurrency(project.etb.solarAvoidedCost, 3) || '-'}</div>
            </div>
            <div className="infoblock">
              <div className="label">{strings.EssAvoidedCost()}</div>
              <div className="value">{typeof project.etb.essAvoidedCost === 'number' ? formatCurrency(project.etb.essAvoidedCost, 3) : '-'}</div>
            </div>
            <div className="infoblock">
              <div className="label">{strings.PvCostPerWdc()}</div>
              <div className="value">{typeof project.etb.pvCostPerWdc === 'number' ? formatCurrency(project.etb.pvCostPerWdc, 2) : '-'}</div>
            </div>
            <div className="infoblock">
              <div className="label">{strings.EssCostPerKwh()}</div>
              <div className="value">{typeof project.etb.essCostPerKwh === 'number' ? formatCurrency(project.etb.essCostPerKwh, 2) : '-'}</div>
            </div>
            <div className="infoblock">
              <div className="label">{strings.WinningStrategy()}</div>
              <div className="value">{project.etb.winningStrategy ? (project.etb.winningStrategy.config === 'stacked' ? strings.EssStrategyStacked() : strings.EssStrategyEa()) : '-'}</div>
            </div>
            <div className="infoblock">
              <div className="label">{strings.EssCapacity()} (kWh)</div>
              <div className="value">{formatNumber(project.etb.winningStrategy?.essCapacity) || '-'}</div>
            </div>
            <div className="infoblock">
              <div className="label">{strings.EssSize()} (kW)</div>
              <div className="value">{formatNumber(project.etb.winningStrategy?.essSize) || '-'}</div>
            </div>
            <div className="infoblock">
              <div className="label">{strings.IntervalData()}</div>
              <div className="value">
                {project.etb.simulatedIntervals?.url
                  ? <a href={fileHref(project.etb.simulatedIntervals)} target="_blank" rel="noreferrer">{strings.Download()}</a>
                  : '-'}
              </div>
            </div>
          </div>
          <h4>{strings.Inputs()}</h4>
          <div className="grid">
            <div className="infoblock">
              <div className="label">{strings.Utility()}</div>
              <div className="value">{project.etb.inputs?.utility || '-'}</div>
            </div>
            <div className="infoblock">
              <div className="label">{strings.RateTariff()}</div>
              <div className="value">{project.etb.inputs?.rateTariff || '-'}</div>
            </div>
            <div className="infoblock">
              <div className="label">{strings.RateSwitchCca()}</div>
              <div className="value">{project.etb.inputs?.cca || '-'}</div>
            </div>
            <div className="infoblock">
              <div className="label">{strings.CcaTariff()}</div>
              <div className="value">{project.etb.inputs?.ccaTariff || '-'}</div>
            </div>
            <div className="infoblock">
              <div className="label">{strings.SystemSize()} (kW)</div>
              <div className="value">{formatNumber(project.etb.inputs?.systemSize) || '-'}</div>
            </div>
            <div className="infoblock">
              <div className="label">{strings.RackingType()}</div>
              <div className="value">{project.etb.inputs?.rackingType || '-'}</div>
            </div>
            <div className="infoblock">
              <div className="label">{strings.BuildingType()}</div>
              <div className="value">{buildingTypes[project.etb.inputs?.buildingType || ''] || project.etb.inputs?.buildingType || '-'}</div>
            </div>
            <div className="infoblock">
              <div className="label">{strings.AnnualConsumption()} (kWh)</div>
              <div className="value">{formatNumber(project.etb.inputs?.annualConsumption) || '-'}</div>
            </div>
            <div className="infoblock">
              <div className="label">{strings.IntervalDataMode()}</div>
              <div className="value">{project.etb.inputs?.intervalDataMode || '-'}</div>
            </div>
          </div>
          {project.etb.provenance && (
            <>
              <h4>{strings.LoadProfileParameters()}</h4>
              <div className="grid">
                <div className="infoblock">
                  <div className="label">{strings.Randomized()}</div>
                  <div className="value">{project.etb.provenance.randomized ? strings.Yes() : strings.No()}</div>
                </div>
                <div className="infoblock">
                  <div className="label">{strings.SimulationStartDate()}</div>
                  <div className="value">{formatDate(project.etb.provenance.startDate) || '-'}</div>
                </div>
                <div className="infoblock">
                  <div className="label">{strings.DataSource()}</div>
                  <div className="value">{project.etb.provenance.dataSource || '-'}</div>
                </div>
                <div className="infoblock">
                  <div className="label">{strings.Sector()}</div>
                  <div className="value">{project.etb.provenance.sector || '-'}</div>
                </div>
                <div className="infoblock">
                  <div className="label">{strings.ResolvedBuildingType()}</div>
                  <div className="value">{buildingTypes[project.etb.provenance.buildingType || ''] || project.etb.provenance.buildingType || '-'}</div>
                </div>
                <div className="infoblock">
                  <div className="label">{strings.Gisjoin()}</div>
                  <div className="value">{project.etb.provenance.countyId || '-'}</div>
                </div>
                <div className="infoblock">
                  <div className="label">{strings.ClimateZone()}</div>
                  <div className="value">{project.etb.provenance.climateZone || '-'}</div>
                </div>
                <div className="infoblock">
                  <div className="label">{strings.State()}</div>
                  <div className="value">{project.etb.provenance.state || '-'}</div>
                </div>
                <div className="infoblock">
                  <div className="label">{strings.ComstockRelease()}</div>
                  <div className="value">{project.etb.provenance.comstockRelease || '-'}</div>
                </div>
                <div className="infoblock">
                  <div className="label">{strings.Fallbacks()}</div>
                  <div className="value">{project.etb.provenance.fallbacksUsed?.length ? project.etb.provenance.fallbacksUsed.join(', ') : '-'}</div>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </EnrichmentsSectionStyled>
  );
};